import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Link,
  Text,
} from '@chakra-ui/react';
// eslint-disable-next-line no-restricted-imports
import { Field, Form, Formik } from 'formik';
import { FC, useState } from 'react';
import { Button } from 'src/components/atoms/Button';
import { ErrorNotifBox } from 'src/components/atoms/ErrorNotifBox';
import { useFontErrorColor } from 'src/hooks/colors/useErrorColor';
import { FormikField } from './types';
import { validateCode } from './validators';

type MfaCodeProps = {
  description: string;
  hint?: string;
  onSubmit: (code: string) => Promise<void>;
  onCancel: () => void;
};

/**
 * Single verification-code form shared by the email OTP and
 * authenticator app MFA steps of the sign-in flow.
 */
export const MfaCode: FC<MfaCodeProps> = ({
  description,
  hint,
  onSubmit,
  onCancel,
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const errorMessageColor = useFontErrorColor();

  return (
    <Formik
      initialValues={{
        code: '',
      }}
      onSubmit={async (values) => {
        try {
          setErrorMessage(null);
          await onSubmit(values.code.trim());
        } catch (error) {
          if (error instanceof Error) {
            setErrorMessage(error.message);
          }
        }
      }}>
      {(props) => (
        <Form>
          <Box pb={2}>
            <Text>{description}</Text>
          </Box>
          <Field name="code" validate={validateCode}>
            {({ field, form }: FormikField) => (
              <FormControl
                my={4}
                isInvalid={form.errors.code && form.touched.code}>
                <FormLabel htmlFor="code">Verification code</FormLabel>
                <Input
                  {...field}
                  id="code"
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  placeholder="123456"
                />
                <FormErrorMessage color={errorMessageColor}>
                  {form.errors.code}
                </FormErrorMessage>
              </FormControl>
            )}
          </Field>
          {hint && (
            <Text fontSize="sm" color="gray.500" mb={2}>
              {hint}
            </Text>
          )}
          {errorMessage && <ErrorNotifBox title={errorMessage} />}
          <Flex direction="row" gap={4} mt={6}>
            <Link
              display="flex"
              alignItems="center"
              justifyContent="center"
              width="50%"
              fontFamily="marketingPage"
              fontSize="16px"
              fontWeight="semibold"
              onClick={onCancel}
              color="gray.500"
              _hover={{ textDecoration: 'underline' }}>
              Back to Login
            </Link>
            <Button
              buttonName="mfa-confirm-code"
              width="50%"
              variant="primary"
              fontFamily="marketingPage"
              fontSize="16px"
              isLoading={props.isSubmitting}
              type="submit">
              Verify
            </Button>
          </Flex>
        </Form>
      )}
    </Formik>
  );
};
